// src/components/charts/SalesByDayChart.tsx
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip
} from "chart.js";
import { IconButton } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ptBR } from "date-fns/locale/pt-BR";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { FiRotateCcw } from "react-icons/fi";
import styled from "styled-components";
import { SalesByDayResponse } from "../../common/interfaces";
import api from "../../services/api";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip
);

const brl = (n: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(n || 0);

const cssVar = (name: string, fallback: string) => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v || fallback;
};

type Props = {
  /** Quantidade de dias no histórico (default 14) */
  days?: number;
  /** Altura do gráfico em px (default 220) */
  height?: number;
};

const toApiDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const SalesByDayChart: React.FC<Props> = ({ days = 14, height = 220 }) => {
  const [payload, setPayload] = useState<SalesByDayResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef<any>(null);

  // filtro de intervalo de datas (opcional)
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const usingRange = Boolean(fromDate && toDate);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const params =
          fromDate && toDate
            ? { from: toApiDate(fromDate), to: toApiDate(toDate) }
            : { days };
        const { data } = await api.get<SalesByDayResponse>(
          "/dashboard/sales-by-day",
          { params }
        );
        if (mounted) setPayload(data);
      } catch (e) {
        console.error("Erro ao buscar vendas por dia:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [days, fromDate, toDate]);

  const clearRange = () => {
    setFromDate(null);
    setToDate(null);
  };

  const totals = payload?.totals;
  const series = payload?.series ?? [];

  // Só desenha a linha de canceladas quando houver alguma no período —
  // uma linha vermelha zerada o tempo todo é ruído visual.
  const hasCanceled = series.some((s) => (s.canceledCount ?? 0) > 0);

  const data = useMemo(() => {
    const accent = cssVar("--accent", "#2b7bff");
    const accent3 = cssVar("--accent-3", "#22d3ee");
    const rose = cssVar("--accent-rose", "#fb7185");

    // gradiente "scriptable" (suportado pelo chart.js) construído a partir do
    // chartArea — evita o problema de passar uma função como `data`.
    const fillGradient = (context: any) => {
      const chart = context.chart;
      const { ctx, chartArea } = chart;
      if (!chartArea) return "rgba(43,123,255,0.18)";
      const g = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      g.addColorStop(0, "rgba(43,123,255,0.32)");
      g.addColorStop(1, "rgba(34,211,238,0.02)");
      return g;
    };

    return {
      labels: series.map((s) => s.label),
      datasets: [
        {
          label: "Receita",
          data: series.map((s) => s.total),
          counts: series.map((s) => s.count),
          borderColor: accent,
          backgroundColor: fillGradient,
          fill: true,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: accent3,
          pointHoverBorderColor: "#fff",
          pointHoverBorderWidth: 2
        } as any,
        ...(hasCanceled
          ? [
              {
                label: "Cancelado",
                data: series.map((s) => s.canceledTotal ?? 0),
                counts: series.map((s) => s.canceledCount ?? 0),
                borderColor: rose,
                backgroundColor: "transparent",
                fill: false,
                tension: 0.4,
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 5,
                pointHoverBackgroundColor: rose,
                pointHoverBorderColor: "#fff",
                pointHoverBorderWidth: 2
              } as any
            ]
          : [])
      ]
    };
  }, [series, hasCanceled]);

  const options: ChartOptions<"line"> = useMemo(() => {
    const grid = cssVar("--border", "#e6e8f4");
    const textMuted = cssVar("--text-muted", "#5b6079");
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          // legenda só aparece quando existe a segunda linha (canceladas),
          // pra diferenciar as cores; com uma linha só é redundante.
          display: hasCanceled,
          labels: {
            color: textMuted,
            usePointStyle: true,
            pointStyle: "line",
            font: { size: 11 }
          }
        },
        title: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => `Dia ${items[0]?.label ?? ""}`,
            label: (ctx) => {
              const value = ctx.parsed.y as number;
              const counts = (ctx.dataset as any).counts as number[];
              const count = counts?.[ctx.dataIndex] ?? 0;
              const plural = count === 1 ? "venda" : "vendas";
              const prefix = hasCanceled ? `${ctx.dataset.label}: ` : "";
              return `${prefix}${brl(value)}  •  ${count} ${plural}`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: textMuted, font: { size: 11 }, maxRotation: 0 },
          border: { display: false }
        },
        y: {
          beginAtZero: true,
          grid: { color: grid },
          border: { display: false },
          ticks: {
            color: textMuted,
            font: { size: 11 },
            callback: (v) => {
              const n = Number(v);
              return n >= 1000 ? `${(n / 1000).toFixed(0)}k` : `${n}`;
            }
          }
        }
      }
    };
  }, [hasCanceled]);

  return (
    <Wrapper>
      <Head>
        <div>
          <Eyebrow>Vendas por dia</Eyebrow>
          <HeadTitle>
            {usingRange
              ? `Intervalo selecionado (${payload?.window.days ?? 0} dias)`
              : `Últimos ${payload?.window.days ?? days} dias`}
          </HeadTitle>
        </div>
        <Kpis>
          <Kpi>
            <KpiLabel>Hoje</KpiLabel>
            <KpiValue>{brl(totals?.todayRevenue ?? 0)}</KpiValue>
            <KpiSub>
              {totals?.todayCount ?? 0}{" "}
              {(totals?.todayCount ?? 0) === 1 ? "venda" : "vendas"}
            </KpiSub>
          </Kpi>
          <Divider />
          <Kpi>
            <KpiLabel>Período</KpiLabel>
            <KpiValue>{brl(totals?.revenue ?? 0)}</KpiValue>
            <KpiSub>
              {totals?.count ?? 0}{" "}
              {(totals?.count ?? 0) === 1 ? "venda" : "vendas"}
            </KpiSub>
          </Kpi>
          <Divider />
          <Kpi>
            <KpiLabel>Média/dia</KpiLabel>
            <KpiValue>{brl(totals?.avgPerDay ?? 0)}</KpiValue>
            <KpiSub>&nbsp;</KpiSub>
          </Kpi>
        </Kpis>
      </Head>

      <FilterBar>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <DatePicker
            label="Data inicial"
            value={fromDate}
            onChange={setFromDate}
            format="dd/MM/yyyy"
            maxDate={toDate ?? undefined}
            slotProps={{
              textField: { size: "small", sx: { width: 160 } }
            }}
          />
          <DatePicker
            label="Data final"
            value={toDate}
            onChange={setToDate}
            format="dd/MM/yyyy"
            minDate={fromDate ?? undefined}
            slotProps={{
              textField: { size: "small", sx: { width: 160 } }
            }}
          />
        </LocalizationProvider>
        {usingRange && (
          <IconButton
            size="small"
            onClick={clearRange}
            title="Limpar filtro"
            aria-label="Limpar filtro"
          >
            <FiRotateCcw size={16} />
          </IconButton>
        )}
      </FilterBar>

      <ChartArea style={{ height }}>
        {loading ? (
          <Placeholder>Carregando…</Placeholder>
        ) : (totals?.count ?? 0) === 0 && !hasCanceled ? (
          <Placeholder>Sem vendas no período.</Placeholder>
        ) : (
          <Line ref={chartRef} data={data as any} options={options} />
        )}
      </ChartArea>
    </Wrapper>
  );
};

export default SalesByDayChart;

/* ----------------------------- styles ----------------------------- */
const Wrapper = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: var(--shadow);
  padding: 20px 22px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: var(--gradient-cyan);
    opacity: 0.9;
  }
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Eyebrow = styled.span`
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-dim);
`;

const HeadTitle = styled.h2`
  margin: 4px 0 0;
  font-family: var(--font-display);
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text);
`;

const Kpis = styled.div`
  display: flex;
  align-items: stretch;
  gap: 18px;
`;

const Kpi = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 96px;
`;

const KpiLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-muted);
`;

const KpiValue = styled.span`
  font-family: var(--font-display);
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.01em;
`;

const KpiSub = styled.span`
  font-size: 0.72rem;
  color: var(--text-dim);
`;

const Divider = styled.span`
  width: 1px;
  background: var(--border);
`;

const ChartArea = styled.div`
  position: relative;
  width: 100%;
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-dim);
  font-size: 0.9rem;
`;
